class PureCurrencyConverter {
  constructor() {
    this.baseCurrency = 'ZAR';
    this.currentCurrency = 'ZAR';
    this.exchangeRates = {};
    this.originalPrices = new Map();
    this.isLoading = false;
    this.apiRetryCount = 0;
    this.maxRetries = 3;
    this.isInitialLoad = true;
    
    this.currencySymbols = {
      ZAR: 'R',
      USD: '$',
      GBP: '£',
      EUR: '€',
      RUB: '₽'
    };
    
    // Store references to all currency selectors (desktop and mobile)
    this.selectors = [];
    
    this.init();
  }

  init() {
    // Wait a bit to ensure DOM is fully loaded
    setTimeout(() => {
      this.findAndSetupSelectors();
      this.setupBoostIntegration();
      
      // Fetch exchange rates first, then handle currency detection and setup
      this.fetchExchangeRates().then(() => {
        // Store original prices after we have exchange rates
        this.storeOriginalPrices();
        
        // Handle currency initialization (detection or saved preference)
        this.initializeCurrency();
      }).catch(() => {
        // If fetching rates fails, still proceed with initialization
        this.storeOriginalPrices();
        this.initializeCurrency();
      });
    }, 100);
  }

  findAndSetupSelectors() {
    // Find all currency selector containers
    const desktopSelector = document.querySelector('.pure-currency-selector');
    const mobileSelector = document.querySelector('.pure-currency-mobile-selector');
    
    if (desktopSelector) {
      this.setupSelector(desktopSelector, 'desktop');
    }
    
    if (mobileSelector) {
      this.setupSelector(mobileSelector, 'mobile');
    }
    
    console.log(`Pure Currency: Found ${this.selectors.length} currency selectors`);
  }

  setupSelector(container, type) {
    const selector = {
      container: container,
      type: type,
      currentSpan: container.querySelector('.pure-currency-current'),
      options: container.querySelectorAll('.pure-currency-option'),
      detailsElement: container.querySelector('details'),
      menuList: container.querySelector('.pure-currency-menu, .pure-currency-mobile-menu, .disclosure__list')
    };
    
    this.selectors.push(selector);
    this.setupEventListeners(selector);
  }

  setupEventListeners(selector) {
    const { container, detailsElement, options } = selector;

    if (!detailsElement) {
      console.error('Pure Currency: Missing details element in', selector.type, 'selector');
      return;
    }

    // Handle option selection
    options.forEach((option) => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const selectedCurrency = option.dataset.currency;
        if (selectedCurrency && selectedCurrency !== this.currentCurrency) {
          this.selectCurrency(selectedCurrency);
        }
        
        // Close the details element
        detailsElement.open = false;
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (detailsElement.open && container && !container.contains(e.target)) {
        detailsElement.open = false;
      }
    });

    // Update aria-expanded when details opens/closes
    detailsElement.addEventListener('toggle', () => {
      const summary = detailsElement.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', detailsElement.open ? 'true' : 'false');
      }
    });
  }

  setupBoostIntegration() {
    // Set up Boost widget integration - but don't auto-add currency to prevent ZAR from being added
    if (window.boostWidgetIntegration) {
      window.boostWidgetIntegration.integration = Object.assign({
        // Remove the automatic currency setting to prevent ZAR from being added
        // currency: 'auto'
      }, window.boostWidgetIntegration.integration || {});
    }

    // Listen for Boost AI content changes
    this.setupBoostObserver();
    
    // Hook into Boost AI events if available
    if (window.BoostPFS) {
      this.setupBoostEventListeners();
    } else {
      // Wait for Boost to initialize
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          if (window.BoostPFS) {
            this.setupBoostEventListeners();
          }
        }, 1000);
      });
    }
  }

  setupBoostEventListeners() {
    // Hook into Boost AI search events
    if (window.BoostPFS && window.BoostPFS.on) {
      window.BoostPFS.on('boost:search:loaded', () => {
        console.log('Pure Currency: Boost search loaded, re-scanning prices');
        this.handleBoostContentUpdate();
      });
      
      window.BoostPFS.on('boost:filter:loaded', () => {
        console.log('Pure Currency: Boost filter loaded, re-scanning prices');
        this.handleBoostContentUpdate();
      });
      
      window.BoostPFS.on('boost:pagination:loaded', () => {
        console.log('Pure Currency: Boost pagination loaded, re-scanning prices');
        this.handleBoostContentUpdate();
      });
    }
  }

  setupBoostObserver() {
    // Use MutationObserver to detect when Boost AI adds new content
    const observer = new MutationObserver((mutations) => {
      let hasNewContent = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node contains price elements or is a Boost container
              const hasMoneyElements = node.querySelectorAll && node.querySelectorAll('.money').length > 0;
              const isBoostContainer = node.classList && (
                node.classList.contains('boost-pfs') ||
                node.classList.contains('boost-search') ||
                node.classList.contains('boost-filter') ||
                node.classList.contains('boost-pagination') ||
                node.dataset.boostSearch !== undefined
              );
              
              if (hasMoneyElements || isBoostContainer) {
                hasNewContent = true;
              }
            }
          });
        }
      });
      
      if (hasNewContent) {
        console.log('Pure Currency: New content detected, re-scanning prices');
        // Debounce the update to avoid excessive calls
        clearTimeout(this.updateTimeout);
        this.updateTimeout = setTimeout(() => {
          this.handleBoostContentUpdate();
        }, 300);
      }
    });

    // Observe the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  handleBoostContentUpdate() {
    // Re-scan for new price elements (this will skip already stored ones)
    this.storeOriginalPrices();
    
    // Convert new prices if we have exchange rates and we're not in base currency
    if (Object.keys(this.exchangeRates).length > 0 && this.currentCurrency !== this.baseCurrency) {
      this.convertPrices();
    }
  }

  async initializeCurrency() {
    const savedCurrency = localStorage.getItem('pure-currency-selected');
    const userHasSelectedCurrency = localStorage.getItem('pure-currency-user-selected') === 'true';
    
    // If user has previously selected a currency, use that
    if (savedCurrency && userHasSelectedCurrency && ['ZAR', 'USD', 'GBP', 'EUR', 'RUB'].includes(savedCurrency)) {
      this.currentCurrency = savedCurrency;
      this.updateAllUI(savedCurrency);
      
      // Convert prices if we have exchange rates
      if (Object.keys(this.exchangeRates).length > 0) {
        this.convertPrices();
      }
      
      console.log(`Pure Currency: Using saved user preference: ${savedCurrency}`);
    } else if (!userHasSelectedCurrency) {
      // First time visitor - detect currency by location
      console.log('Pure Currency: First time visitor - detecting location...');
      await this.detectCurrencyByLocation();
    }
    
    // Notify that currency initialization is complete
    window.dispatchEvent(new CustomEvent('pure-currency-initialized', {
      detail: { currency: this.currentCurrency }
    }));
    
    // Mark initial load as complete
    this.isInitialLoad = false;
  }

  selectCurrency(currency) {
    this.currentCurrency = currency;
    this.updateAllUI(currency);
    this.saveCurrency(currency);
    
    // Mark that user has manually selected a currency
    localStorage.setItem('pure-currency-user-selected', 'true');
    
    // Only convert prices if we have exchange rates
    if (Object.keys(this.exchangeRates).length > 0) {
      this.convertPrices();
    }
    
    // Dispatch custom event for other integrations (like bond calculator)
    window.dispatchEvent(new CustomEvent('pure-currency-changed', {
      detail: { currency: currency }
    }));
  }

  updateAllUI(currency) {
    // Update all selectors (desktop and mobile)
    this.selectors.forEach(selector => {
      this.updateSelectorUI(selector, currency);
    });
  }

  updateSelectorUI(selector, currency) {
    const { currentSpan, options, type } = selector;
    
    // Update current currency display
    if (currentSpan) {
      currentSpan.textContent = currency;
    }
    
    // Update option selections - both selectors now have simple structure
    options.forEach(option => {
      const optionCurrency = option.dataset.currency;
      const isSelected = optionCurrency === currency;
      
      if (type === 'mobile') {
        // Mobile selector styling - let CSS/HTML handle checkmark via selected class
        option.classList.toggle('selected', isSelected);
        // Always just set currency code - selected class handles checkmark
        option.textContent = optionCurrency;
      } else {
        // Desktop selector styling - simple text, checkmark via CSS ::before
        option.classList.toggle('selected', isSelected);
        option.textContent = optionCurrency; // Always just the currency code
      }
    });
    
    console.log(`Pure Currency: Updated ${type} selector to ${currency} (Desktop+Mobile sync)`);
  }

  // Legacy method for backward compatibility
  updateUI(currency) {
    this.updateAllUI(currency);
  }

  storeOriginalPrices() {
    const moneyElements = document.querySelectorAll('.money');
    
    moneyElements.forEach(element => {
      // Skip if we already have this element stored
      if (this.originalPrices.has(element)) {
        return;
      }
      
      const text = element.textContent.trim();
      // Match currency symbols and numbers - improved regex
      const priceMatch = text.match(/[R$£€₽]?\s*[\d,]+\.?\d*/);
      
      if (priceMatch) {
        let numericPrice = parseFloat(priceMatch[0].replace(/[R$£€₽,\s]/g, ''));
        
        if (!isNaN(numericPrice)) {
          // CRITICAL FIX: Always convert price back to base currency (ZAR) if it's in another currency
          let originalPrice = numericPrice;
          let detectedCurrency = this.baseCurrency;
          
          // Detect current currency from the text
          if (text.includes('$')) {
            detectedCurrency = 'USD';
          } else if (text.includes('£')) {
            detectedCurrency = 'GBP';
          } else if (text.includes('€')) {
            detectedCurrency = 'EUR';
          } else if (text.includes('₽')) {
            detectedCurrency = 'RUB';
          }
          
          // Convert back to base currency if needed
          if (detectedCurrency !== this.baseCurrency && this.exchangeRates[detectedCurrency]) {
            originalPrice = numericPrice / this.exchangeRates[detectedCurrency];
            console.log(`Pure Currency: Converting ${detectedCurrency} ${numericPrice} back to ${this.baseCurrency} ${originalPrice}`);
          }
          
          this.originalPrices.set(element, {
            price: originalPrice,
            originalText: this.formatPrice(originalPrice, this.baseCurrency),
            currency: this.baseCurrency
          });
        }
      }
    });
    
    console.log(`Pure Currency: Found ${this.originalPrices.size} price elements`);
  }

  async detectCurrencyByLocation() {
    try {
      console.log('Pure Currency: Detecting user location for currency selection...');
      
      let countryCode = null;
      
      // Try the main geolocation API with timeout
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
        
        const response = await fetch('https://ipapi.co/json/', { 
          signal: controller.signal 
        });
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          countryCode = data.country_code || data.country;
          console.log('Pure Currency: Detected country:', countryCode);
        }
      } catch (error) {
        console.log('Pure Currency: Primary geolocation API failed, trying backup...');
      }
      
      // If primary failed, try Cloudflare's service (often available)
      if (!countryCode) {
        try {
          const response = await fetch('https://cloudflare.com/cdn-cgi/trace');
          if (response.ok) {
            const text = await response.text();
            const locMatch = text.match(/loc=([A-Z]{2})/);
            if (locMatch) {
              countryCode = locMatch[1];
              console.log('Pure Currency: Detected country via Cloudflare:', countryCode);
            }
          }
        } catch (error) {
          console.log('Pure Currency: Cloudflare geolocation failed');
        }
      }
      
      // Map country codes to currencies
      const currencyMap = this.getCountryCurrencyMap();
      const detectedCurrency = currencyMap[countryCode] || 'ZAR'; // Default to ZAR
      
      console.log(`Pure Currency: Country ${countryCode} mapped to currency ${detectedCurrency}`);
      
      // Set the detected currency
      this.setLocationBasedCurrency(detectedCurrency);
      
    } catch (error) {
      console.error('Pure Currency: Location detection failed:', error);
      // Fallback to ZAR if detection fails
      this.setLocationBasedCurrency('ZAR');
    }
  }

  getCountryCurrencyMap() {
    return {
      // Major English-speaking countries - USD
      'US': 'USD', // United States
      'CA': 'USD', // Canada (many prefer USD for international sites)
      'AU': 'USD', // Australia (USD widely accepted)
      'NZ': 'USD', // New Zealand
      
      // UK and related territories - GBP
      'GB': 'GBP', // United Kingdom
      'IE': 'EUR', // Ireland (uses EUR)
      'IM': 'GBP', // Isle of Man
      'JE': 'GBP', // Jersey
      'GG': 'GBP', // Guernsey
      
      // Eurozone countries - EUR
      'DE': 'EUR', // Germany
      'FR': 'EUR', // France
      'IT': 'EUR', // Italy
      'ES': 'EUR', // Spain
      'NL': 'EUR', // Netherlands
      'BE': 'EUR', // Belgium
      'AT': 'EUR', // Austria
      'PT': 'EUR', // Portugal
      'GR': 'EUR', // Greece
      'FI': 'EUR', // Finland
      'LU': 'EUR', // Luxembourg
      'SI': 'EUR', // Slovenia
      'SK': 'EUR', // Slovakia
      'EE': 'EUR', // Estonia
      'LV': 'EUR', // Latvia
      'LT': 'EUR', // Lithuania
      'CY': 'EUR', // Cyprus
      'MT': 'EUR', // Malta
      
      // Russia and related - RUB
      'RU': 'RUB', // Russia
      'BY': 'RUB', // Belarus (often prefer RUB)
      'KZ': 'RUB', // Kazakhstan (RUB widely used)
      
      // South Africa and region - ZAR (default)
      'ZA': 'ZAR', // South Africa
      'BW': 'ZAR', // Botswana
      'LS': 'ZAR', // Lesotho
      'SZ': 'ZAR', // Eswatini
      'NA': 'ZAR', // Namibia
      
      // Other countries that might prefer USD for international transactions
      'SG': 'USD', // Singapore
      'HK': 'USD', // Hong Kong
      'AE': 'USD', // UAE
      'CH': 'USD', // Switzerland (international preference)
      'NO': 'USD', // Norway
      'SE': 'USD', // Sweden
      'DK': 'USD', // Denmark
      'JP': 'USD', // Japan
      'KR': 'USD', // South Korea
      'TH': 'USD', // Thailand
      'MY': 'USD', // Malaysia
      'ID': 'USD', // Indonesia
      'PH': 'USD', // Philippines
      'VN': 'USD', // Vietnam
      'IN': 'USD', // India
      'CN': 'USD', // China
      'BR': 'USD', // Brazil
      'MX': 'USD', // Mexico
      'AR': 'USD', // Argentina
      'CL': 'USD', // Chile
      'CO': 'USD', // Colombia
      'PE': 'USD', // Peru
      'IL': 'USD', // Israel
      'TR': 'USD', // Turkey
      'EG': 'USD', // Egypt
      'MA': 'USD', // Morocco
      'NG': 'USD', // Nigeria
      'KE': 'USD', // Kenya
      'GH': 'USD', // Ghana
    };
  }

  setLocationBasedCurrency(currency) {
    if (['ZAR', 'USD', 'GBP', 'EUR', 'RUB'].includes(currency)) {
      this.currentCurrency = currency;
      this.updateAllUI(currency);
      this.saveCurrency(currency);
      
      // Convert prices if we have exchange rates
      if (Object.keys(this.exchangeRates).length > 0) {
        this.convertPrices();
      }
      
      console.log(`Pure Currency: Set currency to ${currency} based on location`);
      
      // Show a brief notification that currency was auto-detected
      this.showCurrencyDetectedNotification(currency);
    }
  }

  showCurrencyDetectedNotification(currency) {
    // Create a temporary notification
    const notification = document.createElement('div');
    notification.innerHTML = `Currency set to ${currency} based on your location`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 10px 15px;
      border-radius: 5px;
      font-size: 14px;
      z-index: 10000;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    // Show notification
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 100);
    
    // Hide and remove notification after 3 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  resetPricesToBase() {
    // Reset all prices to base currency (ZAR)
    this.originalPrices.forEach((data, element) => {
      try {
        const originalPrice = data.price;
        const formattedPrice = this.formatPrice(originalPrice, this.baseCurrency);
        
        // Reset to original ZAR price
        element.textContent = data.originalText;
      } catch (error) {
        console.error('Pure Currency: Error resetting price for element:', element, error);
      }
    });
    
    this.currentCurrency = this.baseCurrency;
    this.updateAllUI(this.baseCurrency);
    console.log(`Pure Currency: Reset all prices to ${this.baseCurrency}`);
  }

  clearStoredPrices() {
    // Clear all stored prices to force re-scanning
    this.originalPrices.clear();
    console.log('Pure Currency: Cleared all stored prices');
  }

  saveCurrency(currency) {
    localStorage.setItem('pure-currency-selected', currency);
  }

  async fetchExchangeRates() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.showLoading();
    this.hideError();

    try {
      const cachedRates = this.getCachedRates();
      if (cachedRates) {
        this.exchangeRates = cachedRates;
        this.convertPrices();
        this.hideLoading();
        this.isLoading = false;
        return;
      }

      const apis = [
        // Primary: ExchangeRate-API Pro (most accurate, free tier available)
        `https://v6.exchangerate-api.com/v6/YOUR_API_KEY/latest/${this.baseCurrency}`,
        
        // Secondary: Fixer.io (highly accurate, financial grade)
        `https://api.fixer.io/latest?base=${this.baseCurrency}&access_key=YOUR_FIXER_API_KEY`,
        
        // Tertiary: CurrencyLayer (accurate financial data)
        `https://api.currencylayer.com/live?access_key=YOUR_CURRENCYLAYER_KEY&source=${this.baseCurrency}&format=1`,
        
        // Quaternary: Free ExchangeRate-API (good accuracy, no key needed)
        `https://api.exchangerate-api.com/v4/latest/${this.baseCurrency}`,
        
        // Backup: Open Exchange Rates API
        `https://open.er-api.com/v6/latest/${this.baseCurrency}`,
        
        // Last resort: CurrencyAPI
        `https://api.currencyapi.com/v3/latest?apikey=YOUR_CURRENCYAPI_KEY&base_currency=${this.baseCurrency}`
      ];

      let rates = null;
      
      for (const api of apis) {
        try {
          console.log(`Pure Currency: Trying API: ${api}`);
          const response = await fetch(api);
          
          if (response.ok) {
            const data = await response.json();
            
            // Handle different API response formats
            if (data.rates) {
              rates = data.rates;
              console.log('Pure Currency: Successfully fetched rates from standard API:', rates);
              break;
            } else if (data.conversion_rates) {
              // ExchangeRate-API Pro format
              rates = data.conversion_rates;
              console.log('Pure Currency: Successfully fetched rates from ExchangeRate-API Pro:', rates);
              break;
            } else if (data.quotes) {
              // CurrencyLayer format - convert to standard format
              const quotes = data.quotes;
              rates = { [this.baseCurrency]: 1.0 };
              
              // Convert CurrencyLayer format (ZARUSD: 0.055) to standard format
              for (const [pair, rate] of Object.entries(quotes)) {
                const targetCurrency = pair.substring(3); // Remove base currency prefix
                rates[targetCurrency] = rate;
              }
              console.log('Pure Currency: Successfully fetched rates from CurrencyLayer:', rates);
              break;
            }
          }
        } catch (error) {
          console.warn(`Pure Currency: API failed - ${api}:`, error);
        }
      }

      if (!rates) {
        rates = this.getFallbackRates();
        console.log('Pure Currency: Using fallback rates');
      }

      this.exchangeRates = rates;
      this.cacheRates(rates);
      this.convertPrices();
      this.apiRetryCount = 0;

    } catch (error) {
      console.error('Pure Currency: Error fetching exchange rates:', error);
      this.handleFetchError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  getFallbackRates() {
    // Updated fallback rates (as of late 2024) - should be updated periodically
    return {
      ZAR: 1.0,
      USD: 0.055,    // 1 ZAR ≈ 0.055 USD
      GBP: 0.044,    // 1 ZAR ≈ 0.044 GBP  
      EUR: 0.051,    // 1 ZAR ≈ 0.051 EUR
      RUB: 5.1       // 1 ZAR ≈ 5.1 RUB
    };
  }

  getCachedRates() {
    try {
      const cached = localStorage.getItem('pure-currency-rates');
      if (cached) {
        const { rates, timestamp } = JSON.parse(cached);
        // Use cached rates if less than 10 minutes old (more frequent updates for accuracy)
        if (Date.now() - timestamp < 600000) {
          return rates;
        }
      }
    } catch (error) {
      console.error('Pure Currency: Error loading cached rates:', error);
    }
    return null;
  }

  cacheRates(rates) {
    try {
      localStorage.setItem('pure-currency-rates', JSON.stringify({
        rates: rates,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Pure Currency: Error caching rates:', error);
    }
  }

  handleFetchError() {
    if (this.apiRetryCount < this.maxRetries) {
      this.apiRetryCount++;
      setTimeout(() => this.fetchExchangeRates(), 2000 * this.apiRetryCount);
    } else {
      this.exchangeRates = this.getFallbackRates();
      this.convertPrices();
      this.showError('Using offline exchange rates. Prices may not be current.');
    }
  }

  convertPrices() {
    if (!this.exchangeRates[this.currentCurrency]) {
      console.error('Pure Currency: No exchange rate found for', this.currentCurrency);
      return;
    }

    const rate = this.exchangeRates[this.currentCurrency];
    let convertedCount = 0;

    this.originalPrices.forEach((data, element) => {
      try {
        const convertedPrice = data.price * rate;
        const formattedPrice = this.formatPrice(convertedPrice, this.currentCurrency);
        
        // Replace the entire price including currency symbol
        const originalText = data.originalText;
        const priceMatch = originalText.match(/[R$£€₽]?\s*[\d,]+\.?\d*/);
        
        if (priceMatch) {
          const newText = originalText.replace(priceMatch[0], formattedPrice);
          element.textContent = newText;
          convertedCount++;
        }
      } catch (error) {
        console.error('Pure Currency: Error converting price for element:', element, error);
      }
    });

    console.log(`Pure Currency: Converted ${convertedCount} prices to ${this.currentCurrency}`);
  }

  formatPrice(price, currency) {
    const symbol = this.currencySymbols[currency] || currency;
    
    let formattedNumber;
    if (price < 1 && price > 0) {
      formattedNumber = price.toFixed(4);
    } else if (price < 10) {
      formattedNumber = price.toFixed(2);
    } else {
      formattedNumber = Math.round(price).toLocaleString();
    }
    
    return `${symbol}${formattedNumber}`;
  }

  showLoading() {
    const moneyElements = document.querySelectorAll('.money');
    moneyElements.forEach(element => {
      element.classList.add('pure-currency-loading');
    });
  }

  hideLoading() {
    const moneyElements = document.querySelectorAll('.money');
    moneyElements.forEach(element => {
      element.classList.remove('pure-currency-loading');
    });
  }

  showError(message) {
    const errorElement = document.getElementById('pure-currency-error');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
    }
  }

  hideError() {
    const errorElement = document.getElementById('pure-currency-error');
    if (errorElement) {
      errorElement.style.display = 'none';
    }
  }
}

// Initialize the Pure Currency Converter
let pureCurrencyInstance = null;

document.addEventListener('DOMContentLoaded', () => {
  if (!pureCurrencyInstance) {
    pureCurrencyInstance = new PureCurrencyConverter();
    window.pureCurrencyConverter = pureCurrencyInstance;
    console.log('Pure Currency: Initialized - Desktop + Mobile sync ready');
  }
});

// Also initialize if script loads after DOM is ready
if (document.readyState !== 'loading') {
  if (!pureCurrencyInstance) {
    pureCurrencyInstance = new PureCurrencyConverter();
    window.pureCurrencyConverter = pureCurrencyInstance;
    console.log('Pure Currency: Initialized - Desktop + Mobile sync ready');
  }
}

// Global function for Boost AI to trigger currency conversion
window.convertCurrencyPrices = function() {
  if (window.pureCurrencyConverter) {
    window.pureCurrencyConverter.handleBoostContentUpdate();
  }
};

// Global debug functions
window.debugPureCurrency = function() {
  if (window.pureCurrencyConverter) {
    console.log('Current Currency:', window.pureCurrencyConverter.currentCurrency);
    console.log('Base Currency:', window.pureCurrencyConverter.baseCurrency);
    console.log('Exchange Rates:', window.pureCurrencyConverter.exchangeRates);
    console.log('Original Prices Count:', window.pureCurrencyConverter.originalPrices.size);
    console.log('Original Prices:', window.pureCurrencyConverter.originalPrices);
    console.log('User Has Selected Currency:', localStorage.getItem('pure-currency-user-selected'));
    console.log('Saved Currency:', localStorage.getItem('pure-currency-selected'));
    console.log('Active Selectors:', window.pureCurrencyConverter.selectors.length);
  }
};

window.resetCurrencyPrices = function() {
  if (window.pureCurrencyConverter) {
    window.pureCurrencyConverter.resetPricesToBase();
  }
};

window.clearCurrencyPrices = function() {
  if (window.pureCurrencyConverter) {
    window.pureCurrencyConverter.clearStoredPrices();
    window.pureCurrencyConverter.storeOriginalPrices();
  }
};

// Location-based currency functions
window.testLocationCurrency = function() {
  if (window.pureCurrencyConverter) {
    window.pureCurrencyConverter.detectCurrencyByLocation();
  }
};

window.resetLocationPreference = function() {
  localStorage.removeItem('pure-currency-user-selected');
  localStorage.removeItem('pure-currency-selected');
  console.log('Reset currency preferences - will auto-detect on next page load');
};

window.simulateFirstVisitor = function() {
  localStorage.removeItem('pure-currency-user-selected');
  localStorage.removeItem('pure-currency-selected');
  if (window.pureCurrencyConverter) {
    window.pureCurrencyConverter.detectCurrencyByLocation();
  }
};

window.checkInitializationStatus = function() {
  console.log('=== Currency Initialization Status ===');
  console.log('Pure Currency Converter exists:', !!window.pureCurrencyConverter);
  if (window.pureCurrencyConverter) {
    console.log('Current Currency:', window.pureCurrencyConverter.currentCurrency);
    console.log('Is Initial Load:', window.pureCurrencyConverter.isInitialLoad);
    console.log('Exchange Rates loaded:', Object.keys(window.pureCurrencyConverter.exchangeRates).length > 0);
    console.log('Active Selectors:', window.pureCurrencyConverter.selectors.length);
  }
  console.log('Bond Calculator exists:', !!window.bondCalculator);
  if (window.bondCalculator) {
    console.log('Bond Calculator state:', window.bondCalculator.getState());
  }
  console.log('User has selected currency:', localStorage.getItem('pure-currency-user-selected'));
  console.log('Saved currency:', localStorage.getItem('pure-currency-selected'));
};

// Test synchronization function
window.testCurrencySync = function() {
  if (window.pureCurrencyConverter) {
    console.log('=== Testing Desktop ↔ Mobile Sync ===');
    console.log('Active selectors:', window.pureCurrencyConverter.selectors.length);
    
    window.pureCurrencyConverter.selectors.forEach((selector, index) => {
      console.log(`Selector ${index + 1}: ${selector.type}`);
    });
    
    // Test changing to USD
    console.log('Changing to USD...');
    window.pureCurrencyConverter.selectCurrency('USD');
    
    setTimeout(() => {
      console.log('Changing to GBP...');
      window.pureCurrencyConverter.selectCurrency('GBP');
    }, 2000);
    
    setTimeout(() => {
      console.log('Changing back to ZAR...');
      window.pureCurrencyConverter.selectCurrency('ZAR');
    }, 4000);
  }
};

// Test desktop selector specifically
window.testDesktopSelector = function() {
  if (window.pureCurrencyConverter) {
    console.log('=== Testing Desktop Selector ===');
    
    const desktopSelector = window.pureCurrencyConverter.selectors.find(s => s.type === 'desktop');
    if (desktopSelector) {
      console.log('Desktop selector found:', desktopSelector);
      console.log('Current span:', desktopSelector.currentSpan);
      console.log('Options:', desktopSelector.options);
      
      // Test GBP
      console.log('Setting to GBP...');
      window.pureCurrencyConverter.selectCurrency('GBP');
      
      setTimeout(() => {
        console.log('Current span text:', desktopSelector.currentSpan.textContent);
        console.log('Selected options:', Array.from(desktopSelector.options).filter(opt => opt.classList.contains('pure-currency-option--selected')));
      }, 500);
    } else {
      console.log('Desktop selector not found');
    }
  }
};

// Force refresh selectors
window.refreshCurrencySelectors = function() {
  if (window.pureCurrencyConverter) {
    console.log('=== Refreshing Currency Selectors ===');
    window.pureCurrencyConverter.selectors = [];
    window.pureCurrencyConverter.findAndSetupSelectors();
    console.log('Selectors refreshed:', window.pureCurrencyConverter.selectors.length);
  }
}; 